import React, { useCallback, useEffect, useState } from 'react';
import './PwaSheet.css';
import {
  getDeferredPrompt,
  isIOS,
  isInstalled,
  isMobile,
  isSnoozed,
  promptInstall,
  snooze,
  subscribe,
} from '../../Utils/pwaInstall';

// How long the app stays on screen before the sheet slides up, so it never
// competes with the first paint or a login redirect.
const SHOW_DELAY_MS = 2500;

/**
 * Bottom sheet asking mobile visitors to install FamConnect26 to their home
 * screen. On Chrome/Edge/Android it drives the real `beforeinstallprompt`
 * flow; on iOS - where no such event exists - it shows the bookmark > App Library
 * steps instead. "Later" snoozes it for a week.
 */
function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [hasPrompt, setHasPrompt] = useState(Boolean(getDeferredPrompt()));

  const iosOnly = isIOS() && !hasPrompt;

  useEffect(() => subscribe(() => setHasPrompt(Boolean(getDeferredPrompt()))), []);

  useEffect(() => {
    // Desktop is out of scope, and an installed app has nothing to ask for.
    if (!isMobile() || isInstalled() || isSnoozed()) return undefined;

    // Android waits for the browser to hand us a prompt; iOS has none to wait for.
    if (!hasPrompt && !isIOS()) return undefined;

    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [hasPrompt]);

  const handleLater = useCallback(() => {
    snooze();
    setVisible(false);
  }, []);

  const handleInstall = useCallback(async () => {
    const outcome = await promptInstall();
    // 'dismissed' means the user closed the browser's own dialog - don't nag
    // again this week either.
    if (outcome !== 'accepted') snooze();
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div className="pwa-install-overlay" role="dialog" aria-modal="true" aria-labelledby="pwa-install-title">
      {/* data-no-drag: it is a bottom sheet, not a panel to move out of the way */}
      <div className="pwa-install-sheet" data-no-drag>
        <div className="pwa-install-head">
          <img
            className="pwa-install-icon"
            src={window.getPublicUrl ? window.getPublicUrl('/logo192.png') : '/logo192.png'}
            alt="FamConnect26"
          />
          <div>
            <h2 className="pwa-install-title" id="pwa-install-title">
              Install FamConnect26
            </h2>
            <p className="pwa-install-subtitle">Add it to your home screen</p>
          </div>
        </div>

        {iosOnly ? (
          <>
            <p className="pwa-install-body">
              Instructions for the iPhone/IOS
            </p>
            <ol className="pwa-install-steps">
              <li>Tap the 3 horizontal dots at the bottom of the screen</li>
              <li>Choose &ldquo;Add to Bookmarks&rdquo;</li>
              <li>Go to the App Library - swipe left past your home screens until the App Library search bar shows</li>
              <li>Type famconnect26 in the search bar</li>
              <li>When the name pops up, select FamConnect26 - it will now appear on your home screen</li>
            </ol>
          </>
        ) : (
          <p className="pwa-install-body">
            Install FamConnect26 for a full-screen app, faster loading, and your notification count
            right on the icon.
          </p>
        )}

        <div className="pwa-install-actions">
          <button type="button" className="pwa-install-btn pwa-install-btn--secondary" onClick={handleLater}>
            Later
          </button>
          {!iosOnly && (
            <button type="button" className="pwa-install-btn pwa-install-btn--primary" onClick={handleInstall}>
              Install
            </button>
          )}
          {iosOnly && (
            <button type="button" className="pwa-install-btn pwa-install-btn--primary" onClick={handleLater}>
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default InstallPrompt;
